'use client'
import { PopupWrapper } from '@/components'
import { OTPInput } from '@/components/common'
import { verifyOrderReviewOtp } from '@/features/reviews/services'
import { setCookies } from '@/libs/cookies'
import { useCartStore, useCompareStore } from '@/store'
import { Box, Button, Stack, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

interface PopupOTPConfirmProps {
  isOpen: boolean
  handleClose: () => void
  verificationToken: string | null
  onVerifySuccess: () => void
  onResendOTP?: () => Promise<void>
}

export const PopupOTPConfirm = ({
  isOpen,
  handleClose,
  verificationToken,
  onVerifySuccess,
  onResendOTP,
}: PopupOTPConfirmProps) => {
  const { mergeCart } = useCartStore()
  const { assignToUser } = useCompareStore()
  // Local states
  const [otpValue, setOtpValue] = useState('')
  const [otpError, setOtpError] = useState('')
  const [loading, setLoading] = useState(false)
  const [countdown, setCountdown] = useState(300) // 5 phút = 300 giây
  const [canResend, setCanResend] = useState(false)

  // Countdown timer effect (5 phút cho OTP)
  useEffect(() => {
    if (isOpen && countdown > 0) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            setCanResend(true)
            return 0
          }
          return prev - 1
        })
      }, 1000)
      return () => clearInterval(timer)
    }
  }, [isOpen, countdown])

  // Reset state khi popup đóng/mở
  useEffect(() => {
    if (isOpen) {
      setOtpValue('')
      setOtpError('')
      setCountdown(300)
      setCanResend(false)
    }
  }, [isOpen])

  // Format countdown thành phút:giây
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Handler: Xác thực OTP
  const handleVerify = async () => {
    if (!verificationToken) {
      setOtpError('Không tìm thấy mã xác thực')
      return
    }
    setLoading(true)
    setOtpError('')

    try {
      const result = await verifyOrderReviewOtp({
        otp: otpValue,
        verification_token: verificationToken,
      })

      if (result.status === 'error') {
        setOtpError(result.message || 'Mã OTP không hợp lệ')
        setLoading(false)
        return
      }

      if (result.data && result.data.status === 'SUCCESS') {
        toast.success('Xác thực OTP thành công!')

        // Lưu token vào cookies
        await setCookies(result.data.token)
        await mergeCart()
        await assignToUser()
        onVerifySuccess()
        setLoading(false)
      }
    } catch (error) {
      console.error('Verify OTP error:', error)
      setOtpError('Có lỗi xảy ra khi xác thực OTP')
      setLoading(false)
    }
  }

  // Handler: Gửi lại OTP
  const handleResend = async () => {
    if (!canResend || loading) return

    setLoading(true)
    setOtpError('')

    try {
      if (onResendOTP) {
        await onResendOTP()
        // Reset countdown và state
        setCountdown(300)
        setCanResend(false)
        setOtpValue('')
        toast.success('Đã gửi lại mã OTP')
      } else {
        setOtpError('Chức năng gửi lại OTP chưa được triển khai')
      }
    } catch (error) {
      console.error('Resend OTP error:', error)
      setOtpError('Không thể gửi lại mã OTP')
    } finally {
      setLoading(false)
    }
  }

  return (
    <PopupWrapper isOpen={isOpen} handleClose={handleClose} width="520px" hiddenIcon clickOutSite={false}>
      <Box sx={{ p: 2 }}>
        {/* Title */}
        <Typography
          sx={{
            fontSize: {
              xs: '1.5rem',
              md: '1.75rem',
              lg: '2rem',
            },
            fontWeight: 600,
            textAlign: 'center',
            mb: 2,
          }}
        >
          Nhập mã xác nhận
        </Typography>

        {/* Instructions */}
        <Typography
          sx={{
            fontSize: '0.875rem',
            fontWeight: 300,
            textAlign: 'center',
            mb: 2,
          }}
        >
          Vui lòng nhập đoạn mã gồm 6 chữ số vừa gửi về{' '}
          <span
            style={{
              fontWeight: 'bold',
            }}
          >
            email đặt hàng
          </span>{' '}
          của bạn để xác minh bạn là chủ đơn hàng này:
        </Typography>

        {/* OTP Input */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
          <OTPInput
            length={6}
            value={otpValue}
            onChange={(value) => {
              setOtpValue(value)
              setOtpError('')
            }}
            onComplete={(value) => {
              setOtpValue(value)
              handleVerify()
            }}
            autoFocus
            error={!!otpError}
          />
        </Box>

        {/* Error Message */}
        {otpError && (
          <Typography
            sx={{
              fontSize: {
                xs: '0.75rem',
                md: '0.875rem',
              },
              fontWeight: 300,
              color: 'five.main',
              textAlign: 'center',
              mb: 2,
            }}
          >
            {otpError}
          </Typography>
        )}

        {/* Resend */}
        <Stack direction="row" justifyContent="center" spacing={1} sx={{ mb: 2 }}>
          <Typography
            sx={{
              fontSize: {
                xs: '0.75rem',
                md: '0.875rem',
              },
              fontWeight: 300,
            }}
          >
            Mã hết hạn sau: {formatTime(countdown)} {countdown === 0 && '- '}
          </Typography>
          {countdown === 0 && (
            <Typography
              component="button"
              onClick={handleResend}
              disabled={!canResend || loading}
              sx={{
                fontSize: {
                  xs: '0.75rem',
                  md: '0.875rem',
                },
                fontWeight: 600,
                cursor: canResend && !loading ? 'pointer' : 'not-allowed',
                background: 'none',
                border: 'none',
                padding: 0,
                textDecoration: canResend && !loading ? 'underline' : 'none',
                '&:hover':
                  canResend && !loading
                    ? {
                        opacity: 0.8,
                      }
                    : {},
              }}
            >
              {'Gửi lại mã'}
            </Typography>
          )}
        </Stack>

        {/* Verify Button */}
        <Button
          fullWidth
          variant="contained"
          onClick={handleVerify}
          loading={loading}
          size="large"
          disabled={otpValue.length !== 6 || loading}
          sx={{
            color: 'white',
            borderRadius: '999px',
            fontSize: {
              xs: '0.75rem',
              md: '0.875rem',
            },
            fontWeight: 600,
          }}
        >
          {'Xác minh mã xác nhận'}
        </Button>
      </Box>
    </PopupWrapper>
  )
}
